import { Button, Icon, cn } from "@nexus/ui";
import { HOOK_VARIATIONS_PER_PARENT, type PersonaHookVariationSuggestion } from "@nexus/contracts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export interface HookVariationsComparePanelProps {
  currentHook: string;
  variants: PersonaHookVariationSuggestion[];
  /** Alternates the script can still take; kept hooks beyond this cannot be saved. */
  alternateCapacity: number;
  isConfirming?: boolean;
  onConfirm: (keptIndexes: number[]) => void;
  onDiscard: () => void;
}

function keptIndexesFromFlags(kept: boolean[]): number[] {
  return kept.flatMap((isKept, index) => (isKept ? [index] : []));
}

export function HookVariationsComparePanel({
  currentHook,
  variants,
  alternateCapacity,
  isConfirming = false,
  onConfirm,
  onDiscard,
}: HookVariationsComparePanelProps) {
  const { t } = useTranslation();
  const [kept, setKept] = useState<boolean[]>(() =>
    Array.from({ length: HOOK_VARIATIONS_PER_PARENT }, (_, index) => index < alternateCapacity),
  );

  const keptIndexes = useMemo(() => keptIndexesFromFlags(kept), [kept]);
  const keptCount = keptIndexes.length;
  const confirmDisabled =
    isConfirming || keptCount === 0 || keptCount > alternateCapacity;

  const setKeptAt = (index: number, value: boolean) => {
    setKept((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleCopy = async (hook: string) => {
    try {
      await navigator.clipboard.writeText(hook);
      toast.success(
        t("personaOpportunities.scripts.hookVariations.copied", "Copied hook"),
      );
    } catch {
      toast.error(
        t("personaOpportunities.scripts.hookVariations.copyFailed", "Could not copy hook"),
      );
    }
  };

  return (
    <section
      className="-mx-4 space-y-3 bg-[var(--ai-fill-light)] px-4 py-4"
      data-testid="hook-variations-compare-panel"
    >
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-[15px] font-semibold leading-snug text-foreground">
          {t("personaOpportunities.scripts.hookVariations.title", "3 new hooks")}
        </h3>
        <p className="text-[12px] leading-snug text-muted-foreground">
          {t(
            "personaOpportunities.scripts.hookVariations.alternatesHelper",
            "Kept hooks become alternates on this script. Everything after scene 1 stays identical.",
          )}
        </p>
      </header>

      <p className="text-[13px] leading-relaxed text-foreground">
        <span className="mr-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {t("personaOpportunities.scripts.hookVariations.currentLabel", "CURRENT")}
        </span>
        {currentHook}
      </p>

      <ul className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {variants.map((variant, index) => {
          const isKept = kept[index] ?? false;
          const keepLabel = t(
            "personaOpportunities.scripts.hookVariations.keepAria",
            {
              defaultValue: "Keep {{strategy}}",
              strategy: variant.strategyLabel,
            },
          );
          return (
            <li key={`${variant.strategyLabel}-${index}`}>
              <div
                className={cn(
                  "flex h-full flex-col gap-2.5 rounded-md border bg-background p-3",
                  isKept ? "border-[1.5px] border-[var(--ai)]" : "border-border bg-muted",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={isKept}
                    aria-label={keepLabel}
                    onClick={() => setKeptAt(index, !isKept)}
                    className="flex min-h-11 items-center gap-2 rounded-sm text-left md:min-h-0"
                  >
                    <span
                      className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded-[4px]",
                        isKept
                          ? "bg-[var(--ai-text)] text-white"
                          : "border border-border bg-background",
                      )}
                    >
                      {isKept ? (
                        <Icon name="check" color="inherit" size="xs" />
                      ) : null}
                    </span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--ai-text)]">
                      {variant.strategyLabel}
                    </span>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 min-h-11 min-w-11 text-muted-foreground/70 md:min-h-7 md:min-w-7"
                    onClick={() => void handleCopy(variant.hook)}
                    aria-label={t(
                      "personaOpportunities.scripts.hookVariations.copyAria",
                      "Copy hook",
                    )}
                  >
                    <Icon name="copy" color="inherit" size="xs" />
                  </Button>
                </div>
                <p className="text-[13px] leading-snug text-foreground">{variant.hook}</p>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          disabled={confirmDisabled}
          onClick={() => onConfirm(keptIndexes)}
        >
          {t("personaOpportunities.scripts.hookVariations.saveHooks", {
            defaultValue_one: "Save {{count}} hook",
            defaultValue_other: "Save {{count}} hooks",
            count: keptCount,
          })}
        </Button>
        <Button type="button" variant="secondary" onClick={onDiscard}>
          {t("personaOpportunities.scripts.hookVariations.discard", "Discard")}
        </Button>
      </div>
    </section>
  );
}
